import React, { useCallback, useEffect } from 'react';
import { When, If, Then, Else } from 'react-if';
import { useLocation, useNavigate } from 'react-router-dom';
import { compose } from 'recompose';
import './styles.css';
import { LogoIcon } from '../../elements/icons/Icons';
import { ReactComponent as SidekickLogo } from './new-sidekick-logo.svg';
import { ReactComponent as LogOutIcon } from './logout-icon.svg';
import { ReactComponent as FreelancerTeamsIcon } from './freelancer-teams-icon.svg';
import { ReactComponent as AccountSettingsIcon } from './settings-btn-icon.svg';
import { ReactComponent as AboutIcon } from './about-icon.svg';
import { ReactComponent as InviteIcon } from './invite-btn-icon.svg';
import { useToggle, useMedia } from 'react-use';
import { useAuth } from '../../../hooks/useAuth';
import { doSignOut } from '../../../firebase/api/auth';
import * as ROUTES from '../../../constants/routes';
import { POST_PAGE_TYPES } from '../../../constants/postPageTypes';
import { ReactComponent as PublishedIcon } from './published-btn-icon.svg';
import { ReactComponent as OpportunitiesIcon } from './opportunities-btn-icon.svg';
import { ReactComponent as PendingIcon } from './pending-posts-icon.svg';
import NotificationBadge from '../../elements/NotificationBadge/NotificationBadge';
import classNames from 'classnames';
import { IS_SIDEKICK_APP, IS_VALIDATE_APP } from '../../../constants/projects';
import { withNotificationProvider } from '../../../providers/NotificationProvider';
import { usePostContext, withPostProvider } from '../../../providers/PostProvider';

import './styles.css';
import { useActions } from '../../../hooks/useActions';
import { useDispatch } from 'react-redux';

const PAGES = {
    [POST_PAGE_TYPES.INVITATIONS]: ROUTES.POSTS,
    [POST_PAGE_TYPES.MY_POSTS]: ROUTES.MY_ALL_POSTS,
    [POST_PAGE_TYPES.PENDING_POSTS]: ROUTES.PENDING_POSTS,
    [POST_PAGE_TYPES.ALL_POSTS]: ROUTES.ALL_POSTS,
};

const NavBar = ({
    invitationsCount,
    pendingCount,
    publishedCount,
    toggleCreateTeamPopup,
    toggleInviteFriendsPopup,
    toggleMyTeamPopup,
}) => {
    const { isAdmin, authLoading, setAuthLoading } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const isCollapsed = useMedia('(max-width: 1400px)');
    const [isCreateTeamPopupOpens] = useToggle(false);
    const [isMyTeamPopupOpens] = useToggle(false);
    const [isInviteFriendsPopupOpens] = useToggle(false);
    const { logout } = useActions();
    const dispatch = useDispatch();

    const { currentPostPage, setCurrentPostPage } = usePostContext();

    useEffect(() => {
        for (let [key, value] of Object.entries(PAGES)) {
            if (value === location.pathname) {
                setCurrentPostPage(+key);
            }
        }
    }, []);

    const handleMenuItemClick = useCallback(
        page => () => {
            setCurrentPostPage(page);
            navigate(page);
        },
        [setCurrentPostPage, navigate]
    );

    const menuRenderer = useCallback(
        () => (
            <div className="project-btns-wrap">
                <div
                    className={classNames('btn btn-link main-layout__nav-link', {
                        'main-layout__nav-link--active': location.pathname.includes(
                            ROUTES.MY_ALL_POSTS
                        ),
                    })}
                    onClick={handleMenuItemClick(ROUTES.MY_ALL_POSTS)}
                >
                    <div className="main-layout__nav-link-title">
                        <div className="main-layout__nav-icon main-layout__published-icon">
                            <PublishedIcon />
                        </div>
                        {!isCollapsed && (
                            <If condition={IS_SIDEKICK_APP}>
                                <Then>
                                    <p>Published</p>
                                </Then>
                                <Else>
                                    <p>Published</p>
                                </Else>
                            </If>
                        )}
                    </div>
                    <NotificationBadge unreadCount={publishedCount} />
                </div>
                <div
                    className={classNames('btn btn-link main-layout__nav-link', {
                        'main-layout__nav-link--active': location.pathname.includes(
                            ROUTES.INVITED_POSTS
                        ),
                    })}
                    onClick={handleMenuItemClick(ROUTES.INVITED_POSTS)}
                >
                    <div className="main-layout__nav-link-title">
                        <div className="main-layout__nav-icon main-layout__opportunities-icon">
                            <OpportunitiesIcon />
                        </div>
                        {!isCollapsed && <p>Invited</p>}
                    </div>
                    <NotificationBadge unreadCount={invitationsCount} />
                </div>
                <When condition={isAdmin}>
                    <div
                        className={classNames('btn btn-link main-layout__nav-link', {
                            'main-layout__nav-link--active': location.pathname.includes(
                                ROUTES.PENDING_POSTS
                            ),
                        })}
                        onClick={handleMenuItemClick(ROUTES.PENDING_POSTS)}
                    >
                        <div className="main-layout__nav-link-title">
                            <div className="main-layout__nav-icon main-layout__pending-icon">
                                <PendingIcon />
                            </div>
                            {!isCollapsed && <p>Pending Posts</p>}
                        </div>
                        <NotificationBadge unreadCount={pendingCount} />
                    </div>
                </When>
            </div>
        ),
        [
            pendingCount,
            publishedCount,
            invitationsCount,
            currentPostPage,
            isAdmin,
            isCollapsed,
            handleMenuItemClick,
        ]
    );

    const handleNavigateToAboutPage = useCallback(() => {
        navigate(ROUTES.USER_GUIDE);
    }, [navigate]);

    const handleSignOutButtonClick = useCallback(async () => {
        await doSignOut().then(() => {
            setAuthLoading(true);
            dispatch(logout());
            navigate(ROUTES.SIGN_IN);
        });
    }, [navigate, authLoading, setAuthLoading]);

    return (
        <div
            className={classNames('main-layout__left-nav', {
                'main-layout__left-nav--sidekick': IS_SIDEKICK_APP,
                'main-layout__left-nav--collapsed': isCollapsed,
            })}
        >
            <a
                href="https://www.sidekicknetwork.co"
                target="_blank"
                rel="noopener noreferrer"
                className={classNames({
                    'main-layout__logo': IS_VALIDATE_APP,
                    'main-layout__logo--sidekick': IS_SIDEKICK_APP,
                    'main-layout__logo--collapsed': isCollapsed,
                })}
            >
                <If condition={IS_SIDEKICK_APP}>
                    <Then>
                        <SidekickLogo />
                    </Then>
                    <Else>
                        <LogoIcon />
                    </Else>
                </If>
            </a>
            <div className="navbar-btns-wrap">
                {menuRenderer()}
                <div className="navbar__division-line"></div>
                <div className="settings-btns-wrap">
                    <div
                        className={classNames('btn btn-link main-layout__nav-link', {
                            'main-layout__nav-link--active': isMyTeamPopupOpens === true,
                        })}
                        onClick={toggleMyTeamPopup}
                    >
                        <div className="main-layout__nav-link-title">
                            <div className="main-layout__nav-icon">
                                <FreelancerTeamsIcon />
                            </div>
                            {!isCollapsed && <p>Teams</p>}
                        </div>
                    </div>
                    <div
                        className={classNames('btn btn-link main-layout__nav-link', {
                            'main-layout__nav-link--active': isInviteFriendsPopupOpens === true,
                        })}
                        onClick={toggleInviteFriendsPopup}
                    >
                        <div className="main-layout__nav-link-title">
                            <div className="main-layout__nav-icon main-layout__invite-icon">
                                <InviteIcon />
                            </div>
                            {!isCollapsed && (
                                <If condition={IS_SIDEKICK_APP}>
                                    <Then>
                                        <p>Invite</p>
                                    </Then>
                                    <Else>
                                        <p>Invite</p>
                                    </Else>
                                </If>
                            )}
                        </div>
                    </div>
                    <div
                        className={classNames('btn btn-link main-layout__nav-link', {
                            'main-layout__nav-link--active': isCreateTeamPopupOpens === true,
                        })}
                        onClick={toggleCreateTeamPopup}
                    >
                        <div className="main-layout__nav-link-title">
                            <div className="main-layout__nav-icon">
                                <AccountSettingsIcon />
                            </div>
                            {!isCollapsed && <p>Settings</p>}
                        </div>
                    </div>
                    <div
                        className={classNames('btn btn-link main-layout__nav-link', {
                            'main-layout__about--active': location.pathname.includes(
                                ROUTES.USER_GUIDE
                            ),
                        })}
                        onClick={handleNavigateToAboutPage}
                    >
                        <div className="main-layout__nav-link-title">
                            <div className="main-layout__nav-icon">
                                <AboutIcon />
                            </div>
                            {!isCollapsed && <p>User Guide</p>}
                        </div>
                    </div>
                </div>
            </div>
            <div
                className="btn btn-link main-layout__nav-link main-layout__logout"
                onClick={handleSignOutButtonClick}
            >
                <div className="main-layout__nav-link-title">
                    <div className="main-layout__nav-icon main-layout__logout-icon">
                        <LogOutIcon />
                    </div>
                    {!isCollapsed && <p>Log out</p>}
                </div>
            </div>
        </div>
    );
};

const enhance = compose(withNotificationProvider, withPostProvider);

export default React.memo(enhance(NavBar));
